import { ChordLib } from '@tunasong/music-lib'
import type { Chord, NoteEvent } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import { useEffect, useRef } from 'react'
import { usePlayNotes } from '../player/piano-player.hook.js'

const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM
const QUARTER_NOTE_MS = BEAT_MS
const HALF_NOTE_MS = BEAT_MS * 2
const CHORD_OCTAVE = 4

export interface ChordStab {
  events: NoteEvent[]
  delayMs: number
  durationMs: number
}

export interface BuildAuditionStabsInput {
  prefix: Chord[]
  endingChord: Chord | null
  penultSubstitution: Chord | null
}

const chordToEvents = (chord: Chord, duration: '4n' | '2n'): NoteEvent[] =>
  ChordLib.getNotes(chord).map(n => ({
    type: 'note' as const,
    note: `${n}${CHORD_OCTAVE}` as NoteName,
    duration,
  }))

/**
 * Build the sequence of chord stabs for one cadence audition.
 *  - Prefix chords play sequentially at 100BPM (one quarter-note each, 600ms).
 *  - Ending chord plays as a half-note (1200ms) so the ear hears it land.
 *  - When `penultSubstitution` is non-null, the prefix's LAST chord is replaced
 *    by it before the events are computed (used by the plagal card to swap V→IV).
 *  - When `endingChord` is null (suppressed cadence), returns empty array.
 */
export const buildAuditionStabs = (input: BuildAuditionStabsInput): ChordStab[] => {
  const { prefix, endingChord, penultSubstitution } = input
  if (!endingChord) {
    return []
  }
  const effectivePrefix = penultSubstitution ? [...prefix.slice(0, -1), penultSubstitution] : prefix
  const stabs: ChordStab[] = []
  let cursor = 0
  for (const chord of effectivePrefix) {
    stabs.push({
      events: chordToEvents(chord, '4n'),
      delayMs: cursor,
      durationMs: QUARTER_NOTE_MS,
    })
    cursor += QUARTER_NOTE_MS
  }
  stabs.push({
    events: chordToEvents(endingChord, '2n'),
    delayMs: cursor,
    durationMs: HALF_NOTE_MS,
  })
  return stabs
}

export interface UseCadenceLabPlaybackProps extends BuildAuditionStabsInput {}

/**
 * React hook wrapping `buildAuditionStabs` + `usePlayNotes`. Schedules each
 * stab via `setTimeout`. `stop` clears pending timeouts so closing the drawer
 * or clicking another card's Play button cancels in-flight playback.
 */
export const useCadenceLabPlayback = (props: UseCadenceLabPlaybackProps) => {
  const { playNotes } = usePlayNotes()
  const timeoutsRef = useRef<number[]>([])

  const stop = () => {
    timeoutsRef.current.forEach(t => window.clearTimeout(t))
    timeoutsRef.current = []
  }

  const play = () => {
    stop()
    const stabs = buildAuditionStabs(props)
    for (const stab of stabs) {
      const handle = window.setTimeout(() => playNotes(stab.events), stab.delayMs)
      timeoutsRef.current.push(handle)
    }
  }

  useEffect(() => stop, [])
  return { play, stop }
}
